import { useState, useEffect } from "react";

import { useDarkMode } from "../../context/DarkModeContext";
import {
  InternalContainer,
  ExternalContainer,
} from "../../components/Reusable/Layout";
import { UserDetailModal, CreateAdminModal } from "./component";
import { UsersTable, AdminSearchFilter } from "./component";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchUsers,
  fetchFilterOptions,
  setFilters,
} from "../../features/auth/store/userSlice";
import { RootState } from "../../redux/store";

const AdminManagement = () => {
  const darkMode = useDarkMode();
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const {
    users,
    loading,
    error,
    total,
    perPage,
    currentPage,
    lastPage,
    filters,
    filterOptions,
  } = useAppSelector((state: RootState) => state.users);

  // Filter admin users from the full list
  const adminUsers = users.filter((user: any) => user.role === "admin");
  const adminTotal = adminUsers.length;

  // Fetch users on first mount and when filters change
  useEffect(() => {
    dispatch(
      fetchUsers({
        page: 1,
        role: "admin", // Only fetch admin users
        per_page: 10, // Set appropriate page size
      })
    );
    dispatch(fetchFilterOptions());
  }, [dispatch]);

  // Handle pagination navigation
  const handlePageChange = (newPage: number) => {
    if (!loading && newPage >= 1 && newPage <= lastPage) {
      dispatch(
        fetchUsers({
          page: newPage,
          role: "admin", // Maintain admin filter
        })
      );
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { [field]: value };
    dispatch(setFilters(newFilters));

    // Fetch users with updated filters
    dispatch(fetchUsers({ ...filters, ...newFilters, page: 1 }));
  };

  const handleUserClick = (user: any) => {
    setSelectedUser(user);
    setIsDetailsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailsModalOpen(false);
  };
  const handleCreateAdminClick = () => {
    setIsCreateAdminModalOpen(true);
  };
  const handleCloseCreateAdminModal = () => {
    setIsCreateAdminModalOpen(false);
  };

  const handleUserOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsDetailsModalOpen(false);
    }
  };
  const handleAdminOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsCreateAdminModalOpen(false);
    }
  };
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <ExternalContainer>
      <div className="h-screen w-[1030px]">
        <InternalContainer headerText="Admin Management">
          <div className="flex flex-col">
            <AdminSearchFilter
              onClick={handleCreateAdminClick}
              onFilterChange={handleFilterChange}
              filters={filters}
              filterOptions={filterOptions}
              loading={loading}
            />
            <div className="border border-[#FFFFFF1A]">
              <UsersTable
                users={adminUsers}
                onUserClick={handleUserClick}
                loading={loading}
                currentPage={currentPage}
                totalPages={lastPage}
                onPageChange={handlePageChange}
                total={adminTotal}
                perPage={perPage}
              />
            </div>
          </div>
        </InternalContainer>
      </div>
      {selectedUser && isDetailsModalOpen && (
        <UserDetailModal
          user={selectedUser}
          onClose={handleCloseModal}
          showTabs={true}
          tabItems={["Personal Information", "Role"]}
        />
      )}
      <CreateAdminModal
        isOpen={isCreateAdminModalOpen}
        onClose={handleCloseCreateAdminModal}
        handleOverlayClick={handleAdminOverlayClick}
        handleModalClick={handleModalClick}
      />
    </ExternalContainer>
  );
};

export default AdminManagement;
